import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  IconChecklist, IconChevronDown, IconChevronLeft, IconClipboardCheck, IconExternalLink, IconFileText, IconId,
  IconInfoCircle, IconLink, IconMail, IconPhone, IconPlus, IconShield, IconUserCircle, IconUserOff, IconUsers, type Icon,
} from '@tabler/icons-react';
import { DataError, useRecord } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, StateView, Skeleton, useToast, type ScreenState } from '@hana/ui';
import { compactRow } from './compact';
import { PAD_LINKS, PAD_TABS, myText, padContacts, padInfo, padKpis, padLegal, padPeople, padTodos } from './padDetail';
import { abbr, myChip } from './view';
import s from './customerPad.module.css';

// Mint-Customers-iPad-Detail · hồ sơ khách iPad, tab Tổng quan. Chỉ đọc; nút ghi báo toast. Tiền ẩn (board money=hide).

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function Card({ title, icon: Ico, hint, count, actions, children }: { title: string; icon: Icon; hint?: string; count?: number; actions?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <section className={s.card} aria-label={title}>
      <div className={s.hd}>
        <span className={s.hdIc} aria-hidden="true"><Ico size={18} stroke={1.9} /></span>
        <h2 className={s.h2}>{title}</h2>
        {hint && (
          <span className={s.hint} role="img" aria-label={`Giải thích: ${hint}`} title={hint}>
            <IconInfoCircle size={14} stroke={1.9} aria-hidden="true" />
          </span>
        )}
        {count ? <span className={s.count}>{count}</span> : null}
        <span className={s.grow} />
        {actions}
        <button type="button" className={s.fold} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={() => setOpen(!open)}>
          <IconChevronDown size={18} stroke={1.9} className={open ? s.up : undefined} aria-hidden="true" />
        </button>
      </div>
      {open && <div className={s.body}>{children}</div>}
    </section>
  );
}

export function CustomerPadDetail() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { customerId } = useParams({ strict: false }) as { customerId?: string };
  const { data, error, isLoading, isError, refetch } = useRecord('crmCustomer', customerId);
  const [tab, setTab] = useState<(typeof PAD_TABS)[number]>('Tổng quan');
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const soon = () => toast({ msg: 'Chưa mở ở bản này' });

  let state: ScreenState = 'ready';
  const forced = readState();
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!data) state = 'empty';
  const c = state === 'ready' ? data : undefined;
  const row = c ? compactRow(c) : null;
  const contacts = c ? padContacts(c) : [];
  const todos = c ? padTodos(c) : [];
  const my = c ? myChip(c.my) : null;

  return (
    <Page kind="table">
      <PageHeader
        title={customerId ?? 'Khách hàng'}
        findHint={false}
        lead={
          <Link to="/ezy/customers" className={s.back} aria-label={t('crm.at.11')} title={t('crm.at.11')}>
            <IconChevronLeft size={18} stroke={2} aria-hidden="true" />
          </Link>
        }
        meta={my ? <span className={`${s.chip} ${my.tone === 'warn' ? s.warn : ''}`}>myHANA · {myText(c!)}</span> : undefined}
        sub={
          c ? (
            <span className={s.sub}>
              <span className={s.subItem} title={`MST ${c.mst}`}><IconId size={14} stroke={1.8} aria-hidden="true" />{c.mst}</span>
              <span className={s.subItem} title={`Sale ${c.split || c.sale}`}><span className={s.av} aria-hidden="true">{abbr(c.sale)}</span>{c.split || c.sale}</span>
              <span className={s.subItem} title={`BKG chủ khách ${c.bkg}`}><span className={s.av} aria-hidden="true">{abbr(c.bkg)}</span>{c.bkg}</span>
              <span className={s.subItem} title={`BKG backup ${c.bkg2}`}><IconUserOff size={14} stroke={1.8} aria-hidden="true" />{c.bkg2}</span>
            </span>
          ) : undefined
        }
        actions={
          c ? (
            <>
              <button type="button" className={s.tool} aria-label={t('crm.at.12')} title={t('crm.at.12')} onClick={noWrite}><IconPhone size={18} stroke={2} aria-hidden="true" /></button>
              <button type="button" className={`${s.tool} ${s.line}`} aria-label={t('crm.at.13')} title={t('crm.at.13')} onClick={noWrite}><IconClipboardCheck size={16} stroke={1.8} aria-hidden="true" /></button>
              <button type="button" className={s.open} onClick={soon}><IconExternalLink size={16} stroke={1.8} aria-hidden="true" />{t('crm.cp.open360')}</button>
            </>
          ) : undefined
        }
      />
      <div className={s.page}>
        {c && (
          <>
            <div className={s.kpi}>
              {padKpis(c).map((k) => (
                <div key={k.k} className={s.kpiCell}>
                  <div>{k.k}</div>
                  <b className={k.err ? s.err : undefined}>{k.v}</b>
                </div>
              ))}
            </div>
            <div className={s.tabs} role="tablist" aria-label={t('crm.at.14')}>
              {PAD_TABS.map((t) => (
                <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => (t === 'Tổng quan' ? setTab(t) : toast({ msg: 'Tab này chưa mở ở bản này' }))}>{t}</button>
              ))}
            </div>
          </>
        )}
        <StateView
          state={state}
          title={state === 'empty' ? 'Không tìm thấy khách' : undefined}
          desc={state === 'empty' ? 'Khách này không còn trong danh bạ.' : undefined}
          icon={state === 'empty' ? <IconUsers size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('crm.retry')}</Button> : state === 'empty' ? <Button onClick={() => void navigate({ to: '/ezy/customers' })}>{t('crm.iq.toList')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          {c && row && tab === 'Tổng quan' && (
            <div className={s.grid}>
              <div className={s.col}>
                <Card title={t('crm.at.15')} icon={IconShield} hint={t('crm.at.16')}>
                  <dl style={{ margin: 0 }}>
                    {padLegal(c, row.prov, row.cls).map(([k, v]) => (
                      <div key={k} className={s.kv}><dt>{k}</dt><dd>{v}</dd></div>
                    ))}
                  </dl>
                </Card>
                <Card
                  title={t('crm.at.17')}
                  icon={IconPhone}
                  hint={t('crm.at.18')}
                  actions={<button type="button" className={s.add} onClick={noWrite}><IconPlus size={16} stroke={1.8} aria-hidden="true" />{t('crm.cp.contact')}</button>}
                >
                  {contacts.length === 0 ? (
                    <div className={s.empty}>{t('crm.cp.noContact')}</div>
                  ) : (
                    contacts.map((p) => (
                      <div key={p.name} className={s.ct}>
                        <div className={s.ctTx}>
                          <b>{p.name}</b>
                          <div className={s.lb}>{p.role} · {p.phone}</div>
                          <div className={`${s.ctRoles} ${p.roles.length ? '' : s.none}`}>{p.roles.length ? `Nhận ${p.roles.join(' · ')}` : 'Chưa nhận chứng từ'}</div>
                        </div>
                        <button type="button" className={s.ic} aria-label={`Gọi ${p.name}`} title={`Gọi ${p.name}`} onClick={noWrite}><IconPhone size={16} stroke={1.9} aria-hidden="true" /></button>
                        <button type="button" className={s.ic} aria-label={`Gửi mail ${p.name}`} title={`Gửi mail ${p.name}`} onClick={noWrite}><IconMail size={16} stroke={1.9} aria-hidden="true" /></button>
                      </div>
                    ))
                  )}
                </Card>
              </div>
              <aside className={s.col} aria-label={t('crm.at.19')}>
                <Card title={t('crm.at.20')} icon={IconChecklist} hint={t('crm.at.h1')} count={todos.length}>
                  {todos.length === 0 ? (
                    <div className={s.empty}>{t('crm.cp.noTask')}</div>
                  ) : (
                    todos.map((t) => (
                      <div key={t.k} className={s.todo}>
                        <span className={`${s.box} ${t.tone === 'warn' ? s.warn : ''}`} aria-hidden="true" />
                        <div className={s.todoTx}>
                          {t.k}
                          <div className={s.lb}><span className={s.av} aria-hidden="true">{abbr(t.who)}</span>{t.who}<span className={t.tone === 'err' ? s.err : undefined}>{t.due}</span></div>
                        </div>
                        <button type="button" className={s.act} onClick={noWrite}>{t.act}</button>
                      </div>
                    ))
                  )}
                </Card>
                <Card title={t('crm.at.21')} icon={IconFileText} hint={t('crm.at.22')}>
                  {padInfo(c, row.prov, row.cls).map(([k, v], i) => (
                    <div key={k} className={s.stackKv}><div className={s.k}>{k}</div><div className={s.v} style={i === 0 ? { fontWeight: 600 } : undefined}>{v}</div></div>
                  ))}
                </Card>
                <Card title={t('crm.at.23')} icon={IconUserCircle}>
                  {padPeople(c).map((p) => (
                    <div key={p.who + p.role} className={`${s.person} ${p.none ? s.none : ''}`}>
                      <span className={s.mark} aria-hidden="true">{p.none ? '—' : abbr(p.who)}</span>
                      <div className={s.grow}>
                        <b>{p.who}</b>
                        <div className={s.lb}>{p.role} · theo khách</div>
                      </div>
                      {p.none && <button type="button" className={s.act} aria-label={`Giao ${p.role} · theo khách`} onClick={noWrite}>Giao</button>}
                    </div>
                  ))}
                </Card>
                <Card title={t('crm.at.24')} icon={IconLink}>
                  {PAD_LINKS.map((l) => (
                    <button key={l} type="button" className={s.linkRow} onClick={soon}>{l} ↗</button>
                  ))}
                </Card>
              </aside>
            </div>
          )}
        </StateView>
      </div>
    </Page>
  );
}
